import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  Injector,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  StaticProvider
} from "@angular/core";
import {NHintComponent, NHintOptions, NHintPlacement} from "../components/n-hint/n-hint.component";
import {BehaviorSubject, Observable} from "rxjs";

let activeHint: HintControl;

let showImmediately = false;
let touchOccurred = false;
let delayResetTimerId: any;
let showTimerId: any;


@Directive({
  selector: "[nHint]"
})
export class NHintDirective implements OnDestroy, OnInit {

  @Input() delay = 500;
  /*
  * Touch friendly mode, shows hint on tap. Do not use with buttons.
  * */
  @Input() nHintTouchMode: boolean;
  private control: HintControl;
  private _disabled: boolean;
  private _hintText$ = new BehaviorSubject("");
  private _subtitle$ = new BehaviorSubject("");
  private _sideNote$ = new BehaviorSubject("");

  constructor(private elementRef: ElementRef,
              private cr: ComponentFactoryResolver,
              private injector: Injector,
              private zone: NgZone) {
  }

  @Input("nHint") set hintText(val: string) {
    this._hintText$.next(val);
  }

  @Input("nHintSubtitle") set subtitle(val: string) {
    this._subtitle$.next(val);
  }

  @Input("nHintSideNote") set sideNote(val: string) {
    this._sideNote$.next(val);
  }

  private _placement: NHintPlacement = "top";

  @Input() set placement(val: NHintPlacement) {
    this._placement = val;
    if (this.control) {
      this.control.placement = val;
    }
  }

  @Input()
  set nHintDisabled(val: boolean) {
    this._disabled = val;
    if (!this.control) {
      return;
    }
    if (val) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }

  ngOnInit() {

    this.zone.runOutsideAngular(() => {
      this.control = new HintControl(this.elementRef,
        this.cr, this.injector,
        this._hintText$,
        this._subtitle$,
        this._sideNote$,
        this._placement,
        this.delay,
        this.zone,
        this.nHintTouchMode);

      if (this._disabled) {
        this.control.disable();
      } else {
        this.control.enable();
      }
    });

  }

  ngOnDestroy() {
    if (this.control) {
      this.control.destroy();
      this.control = null;
    }
  }

}

class HintControl {

  private _disabled: boolean;
  private targetElement: ElementRef;
  private hintCmp: ComponentRef<NHintComponent>;

  constructor(hintElement: ElementRef,
              private cr: ComponentFactoryResolver,
              private injector: Injector,
              public title: Observable<string>,
              public subtitle: Observable<string>,
              public sideNote: Observable<string>,
              public placement: NHintPlacement,
              private delay: number,
              private zone: NgZone,
              private touchMode = false) {

    this.targetElement = hintElement;

    const {nativeElement} = this.targetElement;
    nativeElement.addEventListener("mouseenter", this.onMouseEnter);
    if (touchMode) {
      nativeElement.addEventListener("touchstart", this.onTouchStart);
    }
    nativeElement.addEventListener("mouseleave", this.onMouseLeave);
    nativeElement.addEventListener("click", this.onMouseClick);
  }

  disable() {
    this._disabled = true;
    if (showImmediately) {
      clearTimeout(showTimerId);
      showTimerId = null;
    }
  }

  enable() {
    this._disabled = false;
  }

  destroy() {

    if (activeHint && activeHint === this) {
      destroyActiveHint();
    }

    this.targetElement.nativeElement.removeEventListener("mouseenter", this.onMouseEnter);
    if (this.touchMode) {
      this.targetElement.nativeElement.removeEventListener("touchstart", this.onTouchStart);
    }
    this.targetElement.nativeElement.removeEventListener("mouseleave", this.onMouseLeave);
    this.targetElement.nativeElement.removeEventListener("click", this.onMouseClick);

    // cancel all timers and hide hint
    this.hide();
  }

  showHint() {

    if (activeHint === this) {
      return;
    }

    destroyActiveHint();

    if (delayResetTimerId) {
      clearTimeout(delayResetTimerId);
      delayResetTimerId = null;
    }

    const childInjector = Injector.create({
      providers: [
        {
          provide: NHintOptions,
          useValue: new NHintOptions(this.title, this.subtitle, this.sideNote, this.placement, this.targetElement)
        }
      ] as StaticProvider[],
      parent: this.injector
    });

    const cmpFactory = this.cr.resolveComponentFactory(NHintComponent);
    this.hintCmp = cmpFactory.create(childInjector);

    const appRef = this.injector.get(ApplicationRef);
    appRef.attachView(this.hintCmp.hostView);
    document.body.appendChild(this.hintCmp.location.nativeElement);
    this.hintCmp.changeDetectorRef.detectChanges();

    activeHint = this;
    showImmediately = true;
  }

  hide() {
    if (this.hintCmp) {
      this.zone.runGuarded(() => this.hintCmp.destroy());
      this.hintCmp = null;
    }

    if (showTimerId) {
      clearTimeout(showTimerId);
      showTimerId = null;
    }
  }

  private onTouchStart = () => {
    showImmediately = true;
    touchOccurred = true;
  }

  private onMouseEnter = () => {
    // Delay появления
    if (this._disabled || !this.title) {
      return;
    }

    if (showTimerId) {
      clearTimeout(showTimerId);
      showTimerId = null;
    }

    if (showImmediately || (activeHint && activeHint !== this)) {
      this.showHint();
      return;
    }

    showTimerId = setTimeout(() => {
      if (this.title && !activeHint) {
        this.showHint();
      }
    }, this.delay);

  }

  private onMouseLeave = () => {
    destroyActiveHint();
  }

  private onMouseClick = () => {
    if (!this.touchMode || !touchOccurred) {
      destroyActiveHint();
    }
  }
}

function destroyActiveHint() {

  if (showTimerId) {
    clearTimeout(showTimerId);
    showTimerId = null;
  }

  if (activeHint) {
    activeHint.hide();
    activeHint = null;

    delayResetTimerId = setTimeout(() => {
      showImmediately = false;
    }, 2000);
  }

}
